import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ALL_CATEGORIES, OTHERS_CATEGORY } from '../../hooks/usePublicBookingSelection';
import type { PublicBookingService } from '../../types/publicBooking';

interface ServiceMultiSelectProps {
	services: PublicBookingService[];
	selectedIds: number[];
	onToggle: (id: number) => void;
	onRemove: (id: number) => void;
	/** Filter pills rendered above the field; `All` first, `Others` last. */
	categories: string[];
	activeCategory: string;
	onCategoryChange: (category: string) => void;
}

const categoryOf = (service: PublicBookingService): string =>
	service.category?.trim() || OTHERS_CATEGORY;

/**
 * Cheapest price across a service's pricing combinations. The exact price
 * depends on the size and length the customer picks later, so the list shows a
 * "from" figure to set expectations without promising a number.
 */
const priceHint = (service: PublicBookingService): string | null => {
	const prices = service.pricingCombinations.map(pc => pc.price).filter(p => p != null);
	if (prices.length === 0) return null;
	const min = Math.min(...prices);
	const varies = prices.some(p => p !== min);
	return varies ? `from $${min}` : `$${min}`;
};

/**
 * Multi-select service picker for the public booking pages: category filter
 * pills, a searchable panel whose options are grouped by category, and
 * removable tags for what's chosen.
 *
 * Hand-rolled rather than built on Headless UI's Combobox — the trigger is not
 * the search input, an external pill bar drives the same filtered list, and the
 * panel needs sticky group headers with a price column.
 */
const ServiceMultiSelect = ({
	services,
	selectedIds,
	onToggle,
	onRemove,
	categories,
	activeCategory,
	onCategoryChange,
}: ServiceMultiSelectProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [query, setQuery] = useState('');
	const wrapRef = useRef<HTMLDivElement>(null);
	const searchRef = useRef<HTMLInputElement>(null);

	// Close on outside click or Escape.
	useEffect(() => {
		if (!isOpen) return;

		const handlePointerDown = (event: MouseEvent) => {
			if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setIsOpen(false);
		};

		document.addEventListener('mousedown', handlePointerDown);
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('mousedown', handlePointerDown);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOpen]);

	useEffect(() => {
		if (isOpen) searchRef.current?.focus();
	}, [isOpen]);

	const selectedServices = useMemo(
		() =>
			selectedIds
				.map(id => services.find(s => s.id === id))
				.filter((s): s is PublicBookingService => Boolean(s)),
		[selectedIds, services]
	);

	// Category pill + search text, then regrouped for the sticky headers.
	const groupedOptions = useMemo(() => {
		const normalized = query.trim().toLowerCase();
		const filtered = services.filter(service => {
			if (activeCategory !== ALL_CATEGORIES && categoryOf(service) !== activeCategory) {
				return false;
			}
			if (!normalized) return true;
			return (
				service.name.toLowerCase().includes(normalized) ||
				categoryOf(service).toLowerCase().includes(normalized) ||
				(service.description ?? '').toLowerCase().includes(normalized)
			);
		});

		const groups: { category: string; items: PublicBookingService[] }[] = [];
		filtered.forEach(service => {
			const category = categoryOf(service);
			const group = groups.find(g => g.category === category);
			if (group) group.items.push(service);
			else groups.push({ category, items: [service] });
		});
		return groups;
	}, [services, activeCategory, query]);

	const handleCategoryClick = useCallback(
		(category: string) => {
			onCategoryChange(category);
			// Matches the source design: picking a filter reveals what it filtered.
			setIsOpen(true);
		},
		[onCategoryChange]
	);

	const triggerText =
		selectedServices.length === 0
			? 'Type to search services...'
			: selectedServices.length === 1
				? selectedServices[0].name
				: `${selectedServices.length} services selected`;

	return (
		<>
			{/* Only worth showing when there is something to filter between —
			    a provider whose services are all uncategorised would otherwise
			    get a pointless "All / Others" pair. */}
			{categories.length > 2 && (
				<div className="pb__cat-bar">
					{categories.map(category => (
						<button
							key={category}
							type="button"
							className={`pb__cat-pill${category === activeCategory ? ' is-active' : ''}`}
							onClick={() => handleCategoryClick(category)}
						>
							{category}
						</button>
					))}
				</div>
			)}

			<div className="pb-svc" ref={wrapRef}>
				<button
					type="button"
					className={`pb-svc__trigger${isOpen ? ' is-open' : ''}${
						selectedServices.length ? ' has-value' : ''
					}`}
					onClick={() => setIsOpen(open => !open)}
					aria-expanded={isOpen}
					aria-haspopup="listbox"
				>
					<span>{triggerText}</span>
					<span className="pb-svc__arrow">
						<ChevronDownIcon width={14} height={14} strokeWidth={2.5} />
					</span>
				</button>

				{isOpen && (
					<div className="pb-svc__dropdown">
						<div className="pb-svc__search-wrap">
							<input
								ref={searchRef}
								className="pb-svc__search-input"
								type="text"
								value={query}
								onChange={event => setQuery(event.target.value)}
								placeholder="Type to search services..."
								autoComplete="off"
							/>
							<span className="pb-svc__search-icon">
								<MagnifyingGlassIcon width={14} height={14} strokeWidth={2.2} />
							</span>
						</div>

						<div className="pb-svc__list" role="listbox" aria-multiselectable="true">
							{groupedOptions.length === 0 ? (
								<div className="pb-svc__no-results">
									No services match your search.
								</div>
							) : (
								groupedOptions.map(group => (
									<div key={group.category}>
										<div className="pb-svc__cat-header">{group.category}</div>
										{group.items.map(service => {
											const isSelected = selectedIds.includes(service.id);
											const hint = priceHint(service);
											return (
												<button
													key={service.id}
													type="button"
													role="option"
													aria-selected={isSelected}
													className={`pb-svc__item${isSelected ? ' is-selected' : ''}`}
													onClick={() => onToggle(service.id)}
												>
													<span>{service.name}</span>
													{hint && (
														<span className="pb-svc__item-price">
															{hint}
														</span>
													)}
												</button>
											);
										})}
									</div>
								))
							)}
						</div>
					</div>
				)}

				{selectedServices.length > 0 && (
					<div className="pb-svc__tags">
						{selectedServices.map(service => (
							<span key={service.id} className="pb-svc__tag">
								{service.name}
								<button
									type="button"
									title={`Remove ${service.name}`}
									aria-label={`Remove ${service.name}`}
									onClick={event => {
										event.stopPropagation();
										onRemove(service.id);
									}}
								>
									&times;
								</button>
							</span>
						))}
					</div>
				)}
			</div>
		</>
	);
};

export default ServiceMultiSelect;
